import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
  Button,
  Dialog,
  DialogPopup,
  DialogTitle,
  EditableTitleField,
  Frame,
  FrameHeader,
  FramePanel,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
  Text,
  Toggle,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  Widget,
  WidgetInstanceProvider,
  WidgetSurfaceProvider,
  useElementSize,
  useIsMobile,
  useWidgetRegistry,
  type IconName,
  widgetTitle,
  type WidgetConfig,
  type WidgetInstance,
  WidgetSettingsPanel,
} from "@salestrak/ui";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";
import { WidgetDataPanel } from "./widget-data-panel";
import type { DetailViewMode } from "./widget-detail-view-mode";
// Relative import (not the barrel) — the barrel re-exports this dialog.
import type { WidgetDataSlice } from "./widget-drilldown";
import type { Omittable } from "@salestrak/ui";

// One header button cycles the detail view; each mode's icon shows where you ARE,
// the tooltip says where a press goes.
const VIEW_MODE_CYCLE: Record<
  DetailViewMode,
  { icon: IconName; next: DetailViewMode; label: string }
> = {
  chart: { icon: "bar-chart-3", next: "data", label: "Chart view - click for data only" },
  split: { icon: "columns-2", next: "chart", label: "Chart + data - click for chart only" },
  data: { icon: "table-properties", next: "split", label: "Data view - click for chart + data" },
};

// Fresh refreshes stay quiet until this age threshold is crossed.
const STALE_REFRESH_MS = 10 * 60_000;

type WidgetDetailDialogProps = Omittable<{
  instance: WidgetInstance | null;
  open: boolean;
  canManage: boolean;
  onOpenChange: (open: boolean) => void;
  /** Navigate to the previous widget; omit/undefined to disable (at the start). */
  onPrevious?: () => void;
  /** Navigate to the next widget; omit/undefined to disable (at the end). */
  onNext?: () => void;
  onDuplicate: (instance: WidgetInstance) => void;
  onDelete: (id: string) => void;
  /** Persist a config patch — the header breadcrumb rename commits `{ title }`. */
  onConfigChange: (id: string, patch: WidgetConfig) => void;
  /** Chart-element drill-down: when set, the drawer opens on the Data tab
   *  filtered to this slice. Owned by the Dashboard host. */
  dataSlice?: WidgetDataSlice | null;
  /** Monotonic id bumped by the host on every drilldown open — presents the
   *  chart + data split on the Data tab even for an unfiltered open (null
   *  slice). Unchanged by plain Maximize. */
  dataRequestId?: number;
  onClearDataSlice?: () => void;
  /** Monotonic id bumped by the host when the gear is pressed — opens the drawer
   *  on the Settings tab (the gear's whole purpose). Unchanged by plain Maximize. */
  settingsRequestId?: number;
}>;

/**
 * Full-screen widget editor opened by maximizing a widget. A large preview of
 * the widget can fill the body, split with a Settings / Data tabbed drawer, or
 * give the drawer the full body width. Settings is manage-gated, so non-managers
 * see a Data-only drawer.
 *
 * Layout is a `Frame` filling the dialog: the header controls sit directly on
 * the tray (`FrameHeader`), and the preview + drawer region is one
 * `FramePanel`. The panel is a `sunken` well — the tray floats at `popover`,
 * so its panels recess rather than stack more chrome on top.
 */
export function WidgetDetailDialog({
  instance,
  open,
  canManage,
  onOpenChange,
  onPrevious,
  onNext,
  onDuplicate,
  onDelete,
  onConfigChange,
  dataSlice,
  dataRequestId,
  onClearDataSlice,
  settingsRequestId,
}: WidgetDetailDialogProps) {
  const registry = useWidgetRegistry();
  const isMobile = useIsMobile();
  // A def declaring `detailPresentation: "data"` (tables) maximizes its home card
  // straight to the data view — replaces a per-type hardcode that skipped the
  // visually identical favorites-list home card (D13).
  const directDataFullscreen =
    (instance ? registry.get(instance.type)?.detailPresentation === "data" : false) &&
    instance?.config?.home === true;
  const [viewMode, setViewMode] = useState<DetailViewMode>("chart");
  const [panelTab, setPanelTab] = useState("settings");
  // Phones never render the desktop split. If the viewport changes while an
  // open desktop dialog is split, immediately present its drawer as one pane.
  const visibleViewMode = isMobile && viewMode === "split" ? "data" : viewMode;
  const viewModeControl: {
    icon: IconName;
    next: DetailViewMode;
    label: string;
  } = isMobile
    ? visibleViewMode === "chart"
      ? {
          icon: "bar-chart-3",
          next: "data",
          label: "Chart view - click for settings and data",
        }
      : {
          icon: "table-properties",
          next: "chart",
          label: "Settings and data - click for chart",
        }
    : VIEW_MODE_CYCLE[visibleViewMode];
  // Drawer width is user-draggable from the drawer's own left edge; capped so
  // the preview always keeps some room. The cap is
  // measured off the body panel the drawer lives in (measure what you size),
  // not the window.
  const [panelWidth, setPanelWidth] = useState(420);
  const { ref: bodyRef, size: bodySize } = useElementSize<HTMLDivElement>();
  const panelMaxWidth = Math.max(640, Math.floor(bodySize.width * 0.85));

  // Each open starts fresh from its entry point: plain Fullscreen gets the clean
  // chart-only view, and the request-id effects below (declared after, so they win
  // the same commit) specialize a drilldown/gear open. Nothing carries across opens.
  useEffect(() => {
    if (open) {
      setViewMode("chart");
      setPanelTab("settings");
    }
  }, [open]);

  // A dashboard drilldown opens chart + data: the split keeps the clicked chart
  // visible beside the Data panel it filtered. Keyed on the host's monotonic
  // request id, not the slice, so an *unfiltered* open (null slice, e.g. the
  // metric widget) still surfaces Data. Phones have no split — Data fills.
  useEffect(() => {
    if (dataRequestId) {
      setPanelTab("data");
      setViewMode(isMobile ? "data" : "split");
    }
  }, [dataRequestId, isMobile]);

  // A gear open derives the drawer from its entry point: split view on the
  // Settings tab. Keyed on the host's monotonic request id; a plain Maximize
  // leaves the id untouched.
  useEffect(() => {
    if (settingsRequestId) {
      setPanelTab("settings");
      setViewMode(isMobile ? "data" : "split");
    }
  }, [isMobile, settingsRequestId]);

  // Refresh affordance: the icon spins briefly while the widget body remounts
  // (via `key`, so its queries re-run), then settles to a neutral
  // "Refreshed: <time>". No success claim — the dialog can't observe whether the
  // refetch actually completed or errored, so a fixed-timer "Success!" would be
  // a lie (U1). Wiring true completion would require every body to publish its
  // query lifecycle through the view-state channel.
  const [refreshState, setRefreshState] = useState<"idle" | "refreshing">("idle");
  const [refreshTick, setRefreshTick] = useState(0);
  // One timer at a time — the "refreshing" guard above means a second refresh
  // can't start until the first settles, so a single slot is the honest shape.
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(() => Date.now());
  const refresh = useCallback(() => {
    if (refreshState !== "idle") return;
    setRefreshState("refreshing");
    setRefreshTick((n) => n + 1);
    if (refreshTimer.current !== null) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      setLastRefreshedAt(Date.now());
      setRefreshState("idle");
    }, 1000);
  }, [refreshState]);
  useEffect(
    () => () => {
      if (refreshTimer.current !== null) clearTimeout(refreshTimer.current);
    },
    [],
  );
  // Re-render every 30s so stale refresh status appears and ages live even when
  // nothing else changes.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  const refreshedLabel = formatRelativeDate(new Date(lastRefreshedAt).toISOString(), "long");
  const showRefreshLabel =
    refreshState !== "idle" || Date.now() - lastRefreshedAt > STALE_REFRESH_MS;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Near-fullscreen surface, 4rem of backdrop a side: an explicit h/w +
        * `max-w-none` collapses the popup viewport's fr rows, and `p-0` lets
        * the content meet the edges. The stock corner close button is off —
        * the header's own × is the close. */}
      <DialogPopup
        showCloseButton={false}
        size="full" className="p-0"
      >
        {instance && (
          <Frame fill="flex" key={instance.id}>
            <DialogTitle className="sr-only">
              {widgetTitle(instance, registry)}
            </DialogTitle>

            {/* Header: nav · centered breadcrumb · actions — sits directly on
                the tray, outside the panel. The breadcrumb is absolutely
                centered across the full header width (so it stays centered
                regardless of the differing nav/action widths); a flex spacer
                keeps nav left and actions right. */}
            <FrameHeader
              direction="row"
              align="center"
              gap="sm"
              shrink={false}
              minHeight={48}
              paddingX="xs"
              paddingY="none"
              position="relative"
            >
              {/* "Dashboard / <widget>" trail, centered behind the controls.
                  The overlay is inert to the pointer so the empty area passes
                  clicks through to the nav/action buttons, while the centered
                  breadcrumb re-arms its own pointer events and stays clickable
                  to edit — a pass-through overlay needs both layers. */}
              <Box
                pointerEvents="none" position="absolute"
                left={0}
                right={0}
                top={0}
                bottom={0}
                align="center"
                justify="center"
              >
                <Box pointerEvents="auto">
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem>
                        {/* The dialog only opens from its own dashboard view, so
                            closing it returns the user there — satisfying both
                            "go to the dashboard" and "close if already there". */}
                        <BreadcrumbLink
                          render={<Box as="button" />}
                          onClick={() => onOpenChange(false)}
                        >
                          Dashboard
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator>
                        <Text size="sm" variant="muted">
                          /
                        </Text>
                      </BreadcrumbSeparator>
                      <BreadcrumbItem>
                        <EditableTitleField
                          size="sm"
                          weight="semibold"
                          value={widgetTitle(instance, registry)}
                          editable={canManage}
                          onCommit={(next) => onConfigChange(instance.id, { title: next })}
                          aria-label="Widget name"
                        />
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </Box>
              </Box>

              {/* Prev/next nav — individual dark outline buttons so each reads
                  against the matte */}
              <Box direction="row" align="center" gap="xs" shrink={false}>
                <Box background="sunken" rounded="md" border overflow="hidden">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Previous widget"
                    disabled={!onPrevious}
                    onClick={onPrevious}
                  >
                    <Icon name="chevron-left" size="sm" color={onPrevious ? undefined : "muted"} />
                  </Button>
                </Box>
                <Box background="sunken" rounded="md" border overflow="hidden">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Next widget"
                    disabled={!onNext}
                    onClick={onNext}
                  >
                    <Icon name="chevron-right" size="sm" color={onNext ? undefined : "muted"} />
                  </Button>
                </Box>
              </Box>

              <Box fill="flex" />

              <Box direction="row" align="center" gap="xs" shrink={false}>
                {/* Refresh status label lives outside the action, to its left;
                    the action itself is icon-only — matching the other icon-only
                    header actions beside it. */}
                {showRefreshLabel && !isMobile ? (
                  <Text size="xs" variant="muted" truncate={1}>
                    {refreshState === "refreshing"
                      ? "Refreshing..."
                      : `Refreshed: ${refreshedLabel}`}
                  </Text>
                ) : null}
                {isMobile ? null : (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Refresh widget"
                    disabled={refreshState === "refreshing"}
                    onClick={refresh}
                  >
                    {/* Inherits the ghost Button's foreground so the icon matches
                        the other header actions. */}
                    <Icon
                      name="refresh-cw"
                      size="sm"
                      className={refreshState === "refreshing" ? "animate-spin" : undefined}
                    />
                  </Button>
                )}
                {directDataFullscreen ? null : (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        // The pressed wash marks non-default views; the icon
                        // says which one.
                        <Toggle
                          size="sm"
                          aria-label={viewModeControl.label}
                          pressed={visibleViewMode !== "chart"}
                          onPressedChange={() => setViewMode(viewModeControl.next)}
                        />
                      }
                    >
                      <Icon name={viewModeControl.icon} size="sm" />
                    </TooltipTrigger>
                    <TooltipPopup>{viewModeControl.label}</TooltipPopup>
                  </Tooltip>
                )}
                {canManage || isMobile ? (
                  <Menu>
                    <MenuTrigger
                      render={
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Widget actions"
                        />
                      }
                    >
                      <Icon name="more-horizontal" size="sm" />
                    </MenuTrigger>
                    <MenuPopup align="end">
                      {isMobile ? (
                        <MenuItem
                          disabled={refreshState === "refreshing"}
                          onClick={refresh}
                        >
                          <Icon
                            name="refresh-cw"
                            size="xs"
                            className={
                              refreshState === "refreshing" ? "animate-spin" : undefined
                            }
                          />
                          <Text>
                            {refreshState === "refreshing" ? "Refreshing..." : "Refresh"}
                          </Text>
                        </MenuItem>
                      ) : null}
                      {canManage ? (
                        <>
                          {isMobile ? <MenuSeparator /> : null}
                          <MenuItem onClick={() => onDuplicate(instance)}>
                            <Icon name="copy" size="xs" />
                            <Text>Duplicate</Text>
                          </MenuItem>
                          <MenuSeparator />
                          <MenuItem
                            onClick={() => {
                              onDelete(instance.id);
                              onOpenChange(false);
                            }}
                          >
                            <Icon name="trash-2" size="xs" color="destructive" />
                            <Text variant="destructive">Delete</Text>
                          </MenuItem>
                        </>
                      ) : null}
                    </MenuPopup>
                  </Menu>
                ) : null}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Close"
                  onClick={() => onOpenChange(false)}
                >
                  <Icon name="x" size="sm" />
                </Button>
              </Box>
            </FrameHeader>

            {/* Body panel: one FramePanel holding the whole preview + drawer
                region — a `sunken` well, since the tray floats at `popover`.
                Its border outlines the region; the Settings/Data drawer adds
                only a left divider inside it. */}
            <FramePanel
              ref={bodyRef}
              fill="flex"
              overflow="hidden"
              padding="none"
              surface="sunken"
            >
              {/* Everything in the panel sits on its `sunken` fill — the
                  provider tells widget bodies and the data panel's table so
                  their opaque parts (sticky headers, pinned cells) paint the
                  panel rather than cutting a page-colored hole. */}
              <WidgetSurfaceProvider value="sunken">
                {directDataFullscreen ? (
                  <Box direction="column" fill="flex" overflow="hidden" paddingTop="xs">
                    <WidgetDataPanel
                      key={`${instance.id}:${refreshTick}`}
                      instance={instance}
                      slice={null}
                    />
                  </Box>
                ) : (
                  <Box direction="row" fill="flex">
                  {visibleViewMode !== "data" ? (
                    <Box direction="column" fill="flex" padding="lg" gap="sm" overflow="hidden" align="center">
                      {/* Title spans the full panel width, left-aligned — decoupled
                          from the preview below so the input doesn't jump as the
                          side panel resizes or switches view modes. */}
                      <Box direction="column" gap="2xs" shrink={false} align="start" fill="width">
                        <EditableTitleField
                          fill
                          size="lg"
                          weight="bold"
                          align="left"
                          value={widgetTitle(instance, registry)}
                          editable={canManage}
                          onCommit={(next) => onConfigChange(instance.id, { title: next })}
                          aria-label="Widget name"
                        />
                      </Box>
                      {/* The widget body fills all the horizontal room, re-rendering
                          (and re-fetching) when the refresh key bumps. */}
                      {/* `fill` is flex-1 + w-full, so the panel spans the column.
                          `direction="column"` is load-bearing: the widget body root
                          claims its height with `fill` too, and flex-1 is inert in a
                          block parent — a chart would measure itself at 0 tall. */}
                      <Box fill="flex" direction="column" rounded="xl" padding="md" overflow="hidden">
                        <WidgetInstanceProvider
                          instanceId={instance.id}
                          config={instance.config ?? null}
                        >
                          <Widget
                            key={`${instance.id}:${refreshTick}`}
                            module={instance.type}
                            config={instance.config ?? undefined}
                            schemaVersion={instance.schemaVersion}
                            w={12}
                            h={8}
                            onConfigChange={
                              canManage ? (patch) => onConfigChange(instance.id, patch) : undefined
                            }
                          />
                        </WidgetInstanceProvider>
                      </Box>
                    </Box>
                  ) : null}

                  {visibleViewMode !== "chart" ? (
                    <>
                      {/* Drawer: Settings / Data tabs over the matching panels.
                          In split mode, the left border divides it from the
                          preview and doubles as the drag seam; in data mode, it
                          fills the full body panel. */}
                      <Box
                        direction="column"
                        fill={visibleViewMode === "data" ? true : undefined}
                        width={visibleViewMode === "split" ? panelWidth : undefined}
                        minWidth={visibleViewMode === "split" ? 320 : undefined}
                        maxWidth={
                          visibleViewMode === "split" ? panelMaxWidth : undefined
                        }
                        shrink={visibleViewMode === "split" ? false : undefined}
                        background="sunken"
                        border={visibleViewMode === "split" ? ["left"] : undefined}
                        resize={visibleViewMode === "split" ? "left" : undefined}
                        resizeLabel="Resize panel"
                        onResize={({ width }) => setPanelWidth(width)}
                      >
                        <Tabs value={canManage ? panelTab : "data"} onValueChange={setPanelTab}>
                          {/* Underline tabs spanning the full drawer width — the
                              list stretches (`w-full`, overriding the stock
                              `w-fit`) and each tab already grows, so the active
                              bar reaches the edges. The tab indicator IS the
                              underline, so there is no separate baseline border
                              to fight. */}
                          <Box shrink={false}>
                            <TabsList variant="underline" className="w-full">
                              {/* Non-managers get a Data-only drawer: Settings is
                                  an editing surface, and on managed surfaces
                                  (My Work) the toggle panel owns card management. */}
                              {canManage ? (
                                <TabsTab value="settings">
                                  <Icon name="settings" size="sm" />
                                  <Text>Settings</Text>
                                </TabsTab>
                              ) : null}
                              <TabsTab value="data">
                                <Icon name="table-properties" size="sm" />
                                <Text>Data</Text>
                              </TabsTab>
                            </TabsList>
                          </Box>
                          {/* No wrapping scroller: TabsPanel is itself a flex
                              column with its own overflow, so the active tab
                              fills the drawer's remaining height. Settings scrolls
                              within that; the Data tab's DataTable fills it and
                              scrolls internally with its sticky header pinned. */}
                          {canManage ? (
                            <TabsPanel value="settings">
                              <WidgetSettingsPanel
                                instance={instance}
                                canManage={canManage}
                                onConfigChange={onConfigChange}
                              />
                            </TabsPanel>
                          ) : null}
                          <TabsPanel value="data">
                            <WidgetDataPanel
                              instance={instance}
                              slice={dataSlice ?? null}
                              onClearSlice={onClearDataSlice}
                            />
                          </TabsPanel>
                        </Tabs>
                      </Box>
                    </>
                  ) : null}
                  </Box>
                )}
              </WidgetSurfaceProvider>
            </FramePanel>
          </Frame>
        )}
      </DialogPopup>
    </Dialog>
  );
}
